/**
 * The authenticator's QR code, drawn in the browser from the `otpauth://` URI — never fetched (plan-014 C1).
 *
 * The URI carries the person's TOTP seed. Handing it to a QR-code web service would send the one secret that makes
 * the second step a second step to a third party. `uqr` (MIT, no dependencies, already the console's QR encoder)
 * encodes it here and the module matrix becomes one SVG path, exactly as the operator console's enrolment does
 * (`apps/web-staff/src/routes/enrol/qr-code.tsx`). Dark on light in both themes (`personal-security.css`).
 */
import type { ReactNode } from "react";
import { encode } from "uqr";

/** ISO/IEC 18004's quiet zone, in modules. */
const QUIET_ZONE = 4;

export function qrPathOf(text: string): { readonly size: number; readonly path: string } {
  const matrix = encode(text, { ecc: "M", border: QUIET_ZONE });
  let path = "";
  matrix.data.forEach((row, y) => {
    row.forEach((dark, x) => {
      if (dark) path += `M${String(x)} ${String(y)}h1v1h-1z`;
    });
  });
  return { size: matrix.size, path };
}

export function QrCode({ text, label }: { readonly text: string; readonly label: string }): ReactNode {
  const { size, path } = qrPathOf(text);
  return (
    <svg className="ps-qr" viewBox={`0 0 ${String(size)} ${String(size)}`} role="img" aria-label={label} shapeRendering="crispEdges">
      <rect className="ps-qr-light" x={0} y={0} width={size} height={size} />
      <path className="ps-qr-dark" d={path} />
    </svg>
  );
}
